import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  LayoutDashboard, 
  Users, 
  Clock, 
  Calendar, 
  IndianRupee, 
  FileText, 
  BarChart3,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  ChevronLeft,
  Shield,
  Briefcase,
  Target,
  GraduationCap,
  FolderOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const LOGO_URL = "/karya-logo.png?v=3";

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['hr_manager', 'hr_member', 'employee', 'senior_management'] },
  { path: '/employees', label: 'Employees', icon: Users, roles: ['hr_manager', 'hr_member'] },
  { path: '/attendance', label: 'Attendance', icon: Clock, roles: ['hr_manager', 'hr_member', 'employee'] },
  { path: '/leaves', label: 'Leaves', icon: Calendar, roles: ['hr_manager', 'hr_member', 'employee'] },
  { path: '/payroll', label: 'Payroll', icon: IndianRupee, roles: ['hr_manager', 'employee'] },
  { path: '/recruitment', label: 'Recruitment', icon: Briefcase, roles: ['hr_manager', 'hr_member'] },
  { path: '/performance', label: 'Performance', icon: Target, roles: ['hr_manager', 'hr_member', 'employee'] },
  { path: '/training', label: 'Training', icon: GraduationCap, roles: ['hr_manager', 'hr_member', 'employee'] },
  { path: '/documents', label: 'Documents', icon: FolderOpen, roles: ['hr_manager', 'hr_member', 'employee'] },
  { path: '/compliance', label: 'Compliance', icon: Shield, roles: ['hr_manager', 'senior_management'] },
  { path: '/reports', label: 'Reports', icon: BarChart3, roles: ['hr_manager', 'senior_management'] },
  { path: '/holidays', label: 'Holidays', icon: FileText, roles: ['hr_manager', 'hr_member', 'employee'] },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(item => 
    item.roles.includes(user?.role || '')
  );

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'hr_manager': return 'bg-gradient-to-r from-[#1a365d] to-[#2dd4bf] text-white';
      case 'hr_member': return 'bg-gradient-to-r from-[#1a365d] to-[#14b8a6] text-white';
      case 'senior_management': return 'bg-gradient-to-r from-[#0f2744] to-[#1a365d] text-white';
      default: return 'bg-gradient-to-r from-slate-500 to-slate-600 text-white';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'hr_manager': return 'HR Manager';
      case 'hr_member': return 'HR Member';
      case 'senior_management': return 'Senior Mgmt';
      case 'employee': return 'Employee';
      default: return role;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-50 flex h-16 items-center justify-between border-b bg-card/80 backdrop-blur-lg px-4">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setSidebarOpen(true)}
            className="rounded-xl"
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <img
            src={LOGO_URL}
            alt="Karya HRMS"
            className="h-8"
            style={theme === 'dark' ? { filter: 'brightness(0) invert(1)' } : undefined}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-xl" data-testid="theme-toggle-mobile">
            {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 flex flex-col bg-card border-r transition-all duration-300 ease-in-out",
        sidebarCollapsed ? "w-20" : "w-72",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        {/* Logo section */}
        <div className={cn(
          "flex items-center border-b h-16",
          sidebarCollapsed ? "justify-center px-2" : "justify-between px-5"
        )}>
          {!sidebarCollapsed && (
            <img
              src={LOGO_URL}
              alt="Karya HRMS"
              className="h-10"
              style={theme === 'dark' ? { filter: 'brightness(0) invert(1)' } : undefined}
            />
          )}
          {sidebarCollapsed && (
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#1a365d] to-[#2dd4bf] flex items-center justify-center text-white font-bold text-lg">
              K
            </div>
          )}
          <Button 
            variant="ghost" 
            size="icon"
            className="lg:hidden rounded-xl"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            className="hidden lg:flex rounded-xl"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            data-testid="sidebar-toggle"
          >
            <ChevronLeft className={cn("h-5 w-5 transition-transform duration-300", sidebarCollapsed && "rotate-180")} />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-6 px-3 scrollbar-thin">
          <ul className="space-y-2">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={cn(
                      "nav-link",
                      isActive ? "nav-link-active" : "nav-link-inactive",
                      sidebarCollapsed && "justify-center px-0"
                    )}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    <Icon className={cn("h-5 w-5 flex-shrink-0", isActive && "text-white")} />
                    {!sidebarCollapsed && <span>{item.label}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User section */}
        <div className={cn("border-t p-4", sidebarCollapsed && "px-2")}>
          {!sidebarCollapsed ? (
            <div className="mb-4 p-3 rounded-xl bg-gradient-to-br from-muted/50 to-muted">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#1a365d] to-[#2dd4bf] flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-[#1a365d]/20">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{user?.name}</p>
                  <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-medium", getRoleBadgeColor(user?.role))}>
                    {getRoleLabel(user?.role)}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-4 flex justify-center">
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#1a365d] to-[#2dd4bf] flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-[#1a365d]/20">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            </div>
          )}
          
          <div className={cn("flex gap-2", sidebarCollapsed ? "flex-col items-center" : "")}>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleTheme}
              className="hidden lg:flex rounded-xl"
              data-testid="theme-toggle"
            >
              {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleLogout}
              className="rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10"
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className={cn(
        "min-h-screen transition-all duration-300",
        sidebarCollapsed ? "lg:pl-20" : "lg:pl-72"
      )}>
        <div className="container mx-auto px-4 py-6 lg:px-8 lg:py-8 max-w-7xl">
          {children}
        </div>
      </main>
    </div>
  );
};
